import { useState, useEffect } from 'react';
import api from '../services/api';
import { PackagesResponse } from '../types';

interface PackageSetupModalProps {
  orderId: string;
  orderNumber: string;
  onClose: () => void;
  onComplete: () => void;
}

interface OrderItem {
  item_number: number;
  max_width: number;
  max_height: number;
  system_color: string;
  fabric_type: string;
}

export default function PackageSetupModal({ orderId, orderNumber, onClose, onComplete }: PackageSetupModalProps) {
  const [items, setItems] = useState<OrderItem[]>([]);
  const [packageCounts, setPackageCounts] = useState<Record<number, number>>({});
  const [existingPackages, setExistingPackages] = useState<PackagesResponse | null>(null);
  const [printers, setPrinters] = useState<any[]>([]);
  const [selectedPrinter, setSelectedPrinter] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, [orderId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load order items, existing packages, and printers in parallel
      const [orderData, pkgData, printerData] = await Promise.all([
        api.getOrderForShipping(orderId),
        api.getPackages(orderId).catch(() => null),
        api.getPrinters().catch(() => []),
      ]);

      const orderItems = (orderData as any).items || [];
      setItems(orderItems);
      setPrinters(printerData);

      if (pkgData && pkgData.total_packages > 0) {
        setExistingPackages(pkgData);
        // Pre-fill counts from existing packages
        const counts: Record<number, number> = {};
        for (const [itemNum, pkgs] of Object.entries(pkgData.by_item)) {
          counts[Number(itemNum)] = (pkgs as any[]).length;
        }
        setPackageCounts(counts);
      } else {
        // Default: 1 package per item
        const counts: Record<number, number> = {};
        for (const item of orderItems) {
          counts[item.item_number] = 1;
        }
        setPackageCounts(counts);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndPrint = async () => {
    setSaving(true);
    setError('');
    try {
      const itemsPayload = Object.entries(packageCounts).map(([itemNum, count]) => ({
        item_number: Number(itemNum),
        package_count: count,
      }));

      await api.setPackageCounts(orderId, itemsPayload);
      const result = await api.generatePackageLabels(orderId, selectedPrinter || undefined);

      alert(`${result.labels_queued} labels queued for printing!`);
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save packages');
    } finally {
      setSaving(false);
    }
  };

  const handleReprint = async () => {
    setSaving(true);
    setError('');
    try {
      const result = await api.generatePackageLabels(orderId, selectedPrinter || undefined);
      alert(`${result.labels_queued} labels re-queued for printing!`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reprint labels');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Delete all package assignments? This cannot be undone if packages have been scanned.')) return;
    try {
      await api.resetPackages(orderId);
      setExistingPackages(null);
      const counts: Record<number, number> = {};
      for (const item of items) {
        counts[item.item_number] = 1;
      }
      setPackageCounts(counts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset packages');
    }
  };

  const totalPackages = Object.values(packageCounts).reduce((a, b) => a + b, 0);
  const hasScannedPackages = existingPackages ? existingPackages.scanned_in > 0 : false;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 rounded-t-xl z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">Prepare Packages</h2>
              <p className="text-gray-400 text-sm">{orderNumber}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-4">
          {error && (
            <div className="bg-red-900/40 border border-red-700 rounded-lg px-4 py-3 mb-4">
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}

          {existingPackages && (
            <div className="bg-blue-900/30 border border-blue-700 rounded-lg px-4 py-3 mb-4">
              <p className="text-blue-300 text-sm font-medium">
                Packages already set: {existingPackages.total_packages} total
                ({existingPackages.scanned_in} scanned in)
              </p>
              <div className="flex gap-2 mt-2">
                <button
                  onClick={handleReprint}
                  disabled={saving}
                  className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  Reprint Labels
                </button>
                {!hasScannedPackages && (
                  <button
                    onClick={handleReset}
                    className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>
          )}

          <p className="text-gray-400 text-sm mb-4">
            Set the number of physical packages (boxes) for each screen. Each package gets a unique barcode label.
          </p>

          {/* Items list */}
          <div className="space-y-3 mb-4">
            {items.map((item) => (
              <div key={item.item_number} className="flex items-center justify-between p-3 bg-gray-750 rounded-lg border border-gray-600">
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">Screen #{item.item_number}</p>
                  <p className="text-gray-400 text-xs">
                    {Number(item.max_width).toFixed(1)}" x {Number(item.max_height).toFixed(1)}" — {(item.system_color || '').replace(/_/g, ' ')} — {(item.fabric_type || '').replace(/_/g, ' ')}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPackageCounts(prev => ({
                      ...prev,
                      [item.item_number]: Math.max(1, (prev[item.item_number] || 1) - 1),
                    }))}
                    disabled={hasScannedPackages}
                    className="w-8 h-8 flex items-center justify-center bg-gray-600 text-white rounded hover:bg-gray-500 disabled:opacity-50"
                  >
                    -
                  </button>
                  <span className="w-8 text-center text-white font-bold">
                    {packageCounts[item.item_number] || 1}
                  </span>
                  <button
                    onClick={() => setPackageCounts(prev => ({
                      ...prev,
                      [item.item_number]: Math.min(10, (prev[item.item_number] || 1) + 1),
                    }))}
                    disabled={hasScannedPackages}
                    className="w-8 h-8 flex items-center justify-center bg-gray-600 text-white rounded hover:bg-gray-500 disabled:opacity-50"
                  >
                    +
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Total + Printer */}
          <div className="bg-gray-750 rounded-lg p-3 border border-gray-600 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-300 text-sm">Total packages:</span>
              <span className="text-white font-bold text-lg">{totalPackages}</span>
            </div>
            {printers.length > 0 && (
              <div>
                <label className="block text-sm text-gray-300 mb-1">Print to:</label>
                <select
                  value={selectedPrinter}
                  onChange={(e) => setSelectedPrinter(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
                >
                  <option value="">Queue only (print later)</option>
                  {printers.map((p: any) => (
                    <option key={p.id} value={p.id}>
                      {p.name || p.ip_address}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAndPrint}
              disabled={saving || totalPackages === 0 || hasScannedPackages}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors font-medium"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                  Saving...
                </span>
              ) : existingPackages ? (
                'Update & Print Labels'
              ) : (
                'Save & Print Labels'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
